import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Tooltip, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { AddIcon } from 'icons/materialIcons';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getSubteamColor, getSubteamInitialsWithDuplicates } from 'team/shared/utils/subteamUtils';
import TeamSubteamListModal from 'team/components/editMember/editCardTopNavbar/TeamSubteamListModal';
import TeamSubteamCircleMenuModal from 'team/components/subteams/TeamSubteamCircleMenuModal';

const TeamMemberSubteamButton = ({ member, teamId }) => {
    const { currentTeamId } = useTeam();
    const navigate = useNavigate();
    const effectiveTeamId = teamId || currentTeamId;
    const [memberSubteams, setMemberSubteams] = useState([]);
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const [isSubteamModalOpen, setIsSubteamModalOpen] = useState(false);
    const [subteamMenuAnchorEl, setSubteamMenuAnchorEl] = useState(null);
    const [selectedSubteam, setSelectedSubteam] = useState(null);
    const subteamButtonRef = useRef(null);

    const memberUserId = member?.userId || member?.user?.userId || member?.user?._id || member?._id;

    useEffect(() => {
        const fetchMemberSubteams = async () => {
            if (!memberUserId || !effectiveTeamId) return;

            setLoadingSubteams(true);
            try {
                const response = await getTeamMembers(effectiveTeamId, { status: 'active' });

                if (response && response.members) {
                    const foundMember = response.members.find(m => {
                        const candidateUserId = m.userId || m.user?.userId;
                        return candidateUserId && candidateUserId.toString() === memberUserId.toString();
                    });

                    if (foundMember && foundMember.subteamIds && Array.isArray(foundMember.subteamIds)) {
                        const subteams = foundMember.subteamIds.map(subteam => {
                            if (subteam && typeof subteam === 'object' && subteam._id) {
                                return {
                                    _id: subteam._id.toString(),
                                    name: subteam.name || 'Unnamed Subteam',
                                    logoUrl: subteam.logoUrl || '',
                                    description: subteam.description || ''
                                };
                            }
                            return {
                                _id: subteam.toString(),
                                name: 'Subteam',
                                logoUrl: '',
                                description: ''
                            };
                        });
                        setMemberSubteams(subteams);
                    } else {
                        setMemberSubteams([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching member subteams:', error);
                setMemberSubteams([]);
            } finally {
                setLoadingSubteams(false);
            }
        };

        fetchMemberSubteams();
    }, [memberUserId, effectiveTeamId]);

    const handleSubteamDropdownOpen = (e) => {
        e.stopPropagation();
        setIsSubteamModalOpen(true);
    };

    const handleSubteamDropdownClose = () => {
        setIsSubteamModalOpen(false);
    };

    const handleSubteamCircleClick = (event, subteam) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedSubteam(subteam);
        setSubteamMenuAnchorEl(subteamButtonRef.current);
    };

    const handleSubteamMenuClose = () => {
        setSubteamMenuAnchorEl(null);
        setSelectedSubteam(null);
    };

    const handleViewSubteamMembers = () => {
        if (!selectedSubteam?._id) return;
        navigate(`/team/members?subteamId=${selectedSubteam._id}`);
        handleSubteamMenuClose();
    };

    const handleEditSubteam = () => {
        if (!selectedSubteam?._id) return;
        navigate(`/team/subteams/${selectedSubteam._id}/members?tab=general`);
        handleSubteamMenuClose();
    };

    const renderSubteamButtonIcon = () => {
        if (loadingSubteams) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupOutlinedIcon sx={{ fontSize: '1rem', color: '#828282' }} />
                    <Box
                        sx={{
                            backgroundColor: '#f7f7f7',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AddIcon
                            sx={{
                                fontSize: '0.875rem',
                                color: '#828282'
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        if (memberSubteams.length === 0) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupOutlinedIcon
                        sx={{
                            fontSize: '1rem',
                            color: '#828282'
                        }}
                    />
                    <Box
                        sx={{
                            backgroundColor: '#f7f7f7',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <AddIcon
                            sx={{
                                fontSize: '0.875rem',
                                color: '#828282'
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        const subteamsWithInitials = getSubteamInitialsWithDuplicates(memberSubteams);
        const maxVisible = 3;
        const visibleSubteams = subteamsWithInitials.slice(0, maxVisible);
        const remainingCount = subteamsWithInitials.length - maxVisible;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {visibleSubteams.map((subteam, index) => (
                    <Tooltip
                        key={subteam._id}
                        title={subteam.name}
                        placement="top"
                        arrow
                    >
                        <Box
                            onClick={(event) => handleSubteamCircleClick(event, subteam)}
                            data-member-action="subteam-circle"
                            sx={{ display: 'inline-flex', cursor: 'pointer' }}
                        >
                            <Avatar
                                src={subteam.logoUrl}
                                sx={{
                                    width: 25,
                                    height: 25,
                                    position: 'relative',
                                    zIndex: visibleSubteams.length - index,
                                    marginLeft: index > 0 ? '-10px' : 0,
                                    border: '2px solid #ffffff',
                                    backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                    flexShrink: 0,
                                    fontSize: (subteam.displayInitials?.length || 0) > 1 ? '0.65rem' : '0.75rem'
                                }}
                            >
                                {!subteam.logoUrl && subteam.displayInitials}
                            </Avatar>
                        </Box>
                    </Tooltip>
                ))}
                {remainingCount > 0 && (
                    <Tooltip
                        title={`${remainingCount} more subteam${remainingCount > 1 ? 's' : ''}`}
                        placement="top"
                        arrow
                    >
                        <Avatar
                            sx={{
                                width: 25,
                                height: 25,
                                position: 'relative',
                                zIndex: 0,
                                marginLeft: '-10px',
                                border: '2px solid #ffffff',
                                backgroundColor: '#e0e0e0',
                                color: '#666',
                                fontSize: '0.7rem',
                                flexShrink: 0
                            }}
                        >
                            +{remainingCount}
                        </Avatar>
                    </Tooltip>
                )}
                <Box
                    sx={{
                        backgroundColor: '#f7f7f7',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: visibleSubteams.length > 0 ? '8px' : '4px'
                    }}
                >
                    <AddIcon
                        sx={{
                            fontSize: '0.875rem',
                            color: '#828282'
                        }}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Button
                ref={subteamButtonRef}
                variant="contained"
                onClick={(event) => {
                    event.stopPropagation();
                    handleSubteamDropdownOpen(event);
                }}
                data-member-action="subteam"
                sx={{
                    backgroundColor: '#fff',
                    color: '#e0e0e0',
                    fontWeight: '500',
                    borderRadius: '999px',
                    border: '1px #e1e1e1 solid',
                    textTransform: 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 'auto',
                    padding: '6.5px 10px 6.5px 12px',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#fff',
                        color: '#e0e0e0',
                        boxShadow: 'none',
                    },
                    '& .MuiButton-startIcon': {
                        marginRight: 0,
                        marginLeft: 0,
                    }
                }}
                startIcon={renderSubteamButtonIcon()}
                disableRipple
            />
            <TeamSubteamListModal
                isOpen={isSubteamModalOpen}
                onClose={handleSubteamDropdownClose}
                anchorEl={subteamButtonRef.current}
                user={member}
                teamId={effectiveTeamId}
                isCardContext={true}
                onSubteamsUpdated={() => {
                    if (member?.userId && effectiveTeamId) {
                        const fetchMemberSubteams = async () => {
                            setLoadingSubteams(true);
                            try {
                                const response = await getTeamMembers(effectiveTeamId, { status: 'active' });

                                if (response && response.members) {
                                    const foundMember = response.members.find(m => {
                                        const memberUserId = m.userId || m.user?.userId;
                                        return memberUserId === member.userId;
                                    });

                                    if (foundMember && foundMember.subteamIds && Array.isArray(foundMember.subteamIds)) {
                                        const subteams = foundMember.subteamIds.map(subteam => {
                                            if (subteam && typeof subteam === 'object' && subteam._id) {
                                                return {
                                                    _id: subteam._id.toString(),
                                                    name: subteam.name || 'Unnamed Subteam',
                                                    logoUrl: subteam.logoUrl || '',
                                                    description: subteam.description || ''
                                                };
                                            }
                                            return {
                                                _id: subteam.toString(),
                                                name: 'Subteam',
                                                logoUrl: '',
                                                description: ''
                                            };
                                        });
                                        setMemberSubteams(subteams);
                                    } else {
                                        setMemberSubteams([]);
                                    }
                                }
                            } catch (error) {
                                console.error('Error refreshing member subteams:', error);
                            } finally {
                                setLoadingSubteams(false);
                            }
                        };
                        fetchMemberSubteams();
                    }
                }}
            />
            <TeamSubteamCircleMenuModal
                open={Boolean(subteamMenuAnchorEl)}
                anchorEl={subteamMenuAnchorEl}
                onClose={handleSubteamMenuClose}
                onViewMembers={handleViewSubteamMembers}
                onEditSubteam={handleEditSubteam}
            />
        </>
    );
};

export default TeamMemberSubteamButton;
